import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { LessonTutorAvailabilityItem, LessonTutorFilters } from './interfaces'
import type { LessonTutorAvailabilityLabel } from './lesson-create'

import { useListsStore } from '@shared/model'

import { LessonTutorAvailability } from './enums'
import { useLessonCreateStore } from './create/store'
import { useDisciplineStore } from '../store'
import { useTutorsAvailabilityApi } from '../../api/lessons/availability.hook'
import { useLessonCreateSave } from './lesson-create-save.hook'
import {
  TUTOR_AVAILABILITY_LABELS,
  buildLessonTutorOptions,
  getDefaultTutorFilters,
  getLessonTypeCategories,
  getTutorAvailability,
} from './lesson-create'

interface LessonCreateTutorModalReturn {
  refFilters: ComputedRef<LessonTutorFilters | null>
  refTutorId: WritableComputedRef<number | null>
  refTutorOptions: ComputedRef<AppSelectOption<number>[]>
  refIsTutorsLoading: Ref<boolean>
  refDate: ComputedRef<string>
  refSelectedLabel: ComputedRef<LessonTutorAvailabilityLabel | null>
  refIsSelectedBusy: ComputedRef<boolean>
  refIsOffSchedule: ComputedRef<boolean>
  refIsSaveDisabled: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  applyFilters: (filters: LessonTutorFilters) => Promise<void>
  clearFilters: () => Promise<void>
  save: () => Promise<void>
  close: () => void
}

/**
 * The tutor modal of the create page, the last step of adding a lesson, one screen like the edit page's: the list
 * is the tutors matching the chosen start and the filters applied above the select, read when the modal opens and
 * again on Apply filters and Clear; the applied filters are kept while the form is on screen. "-- Not selected --"
 * adds the lesson without a tutor, a tutor off their schedule is added with the warning shown, a busy one cannot be
 * added. Add lesson here creates the lesson and leads back to the Lessons tab.
 */
export const useLessonCreateTutorModal = (): LessonCreateTutorModalReturn => {
  const route = useRoute()
  const store = useLessonCreateStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { createLesson } = useLessonCreateSave()

  const disciplineId = route.params.id as string

  const refTutors = ref<LessonTutorAvailabilityItem[]>([])
  const refIsTutorsLoading = ref<boolean>(false)
  const refDraft = ref<number | null>(store.getTutorId)

  const refDate = computed<string>(() => store.getDate)

  const refIsSaving = computed<boolean>(() => store.isSaving)

  /** The filters applied so far, or null for the discipline's defaults. */
  const refFilters = computed<LessonTutorFilters | null>(() => store.getAppliedFilters)

  const refTutorOptions = computed<AppSelectOption<number>[]>(() =>
    buildLessonTutorOptions(refTutors.value, listsStore.getLists?.tutor.paid_label ?? {}))

  const isListed = (tutorId: number | null): boolean => tutorId !== null && refTutors.value.some(tutor => tutor.id === tutorId)

  /** What the select shows: the pick while the list has it, else "-- Not selected --". */
  const refTutorId = computed<number | null>({
    get: () => (isListed(refDraft.value) ? refDraft.value : null),
    set: (value) => {
      refDraft.value = value
    },
  })

  const refSelectedAvailability = computed<LessonTutorAvailability | null>(() =>
    getTutorAvailability(refTutors.value, refTutorId.value))

  const refSelectedLabel = computed<LessonTutorAvailabilityLabel | null>(() =>
    (refSelectedAvailability.value ? TUTOR_AVAILABILITY_LABELS[refSelectedAvailability.value] : null))

  const refIsSelectedBusy = computed<boolean>(() => refSelectedAvailability.value === LessonTutorAvailability.Unavailable)

  const refIsOffSchedule = computed<boolean>(() => refSelectedAvailability.value === LessonTutorAvailability.OutOfSchedule)

  const refIsSaveDisabled = computed<boolean>(() => refIsTutorsLoading.value || refIsSaving.value || refIsSelectedBusy.value)

  const loadTutors = async (): Promise<void> => {
    const start = store.getStart
    const type = store.getType
    if (!start || !type) return

    const filters = store.getAppliedFilters ?? getDefaultTutorFilters(disciplineStore.getDiscipline?.code)

    refIsTutorsLoading.value = true

    const tutors = await useTutorsAvailabilityApi({
      params: {
        start,
        discipline_id: Number(disciplineId),
        gender: filters.gender,
        special_features: filters.special_features,
        accent: filters.accent,
        categories: filters.categories.length ? filters.categories : getLessonTypeCategories(type),
      },
    })

    refTutors.value = tutors ?? []
    refIsTutorsLoading.value = false

    // A pick the new list no longer has is dropped: the select is back on "-- Not selected --".
    if (!isListed(refDraft.value)) refDraft.value = null
  }

  onMounted(loadTutors)

  const applyFilters = async (filters: LessonTutorFilters): Promise<void> => {
    store.setAppliedFilters(filters)
    await loadTutors()
  }

  const clearFilters = async (): Promise<void> => {
    store.setAppliedFilters(null)
    await loadTutors()
  }

  const save = async (): Promise<void> => {
    if (refIsSaveDisabled.value) return

    store.setTutorId(refTutorId.value)

    const isCreated = await createLesson()
    if (!isCreated) store.setTutorModalOpen(false)
  }

  const close = (): void => {
    if (refIsSaving.value) return

    store.setTutorModalOpen(false)
  }

  return {
    refFilters,
    refTutorId,
    refTutorOptions,
    refIsTutorsLoading,
    refDate,
    refSelectedLabel,
    refIsSelectedBusy,
    refIsOffSchedule,
    refIsSaveDisabled,
    refIsSaving,
    applyFilters,
    clearFilters,
    save,
    close,
  }
}
